<template>
  <BasePageContainer>
    <ul class="list-unstyled members__head p-3 mb-4">
      <user-profile-list-item
        v-if="user"
        :profile="user">
        <div class="d-flex flex-column align-items-end justify-content-between">
          <!-- UserProfileListItemにclickイベントが伝搬しないように @click.stopを追加 -->
          <div
            class="calendar -small d-flex justify-content-center align-items-center"
            @click.stop="() => {}">
            <button
              class="calendar__btn calendar__btn--previous"
              @click="onClickCalendarBtn">
              <span
                class="material-icons calendar__icon">
                arrow_left
              </span>
            </button>
            <span class="ml-1 mr-1">
              <b-form-datepicker
                id="datepicker"
                v-model="selectedDate"
                value-as-date
                hide-header
                right
                :offset="90"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                class="datepicker_now blue" />
            </span>
            <button
              class="calendar__btn calendar__btn--next"
              @click="onClickCalendarBtn">
              <span class="calendar__icon material-icons">
                arrow_right
              </span>
            </button>
          </div>
        </div>
      </user-profile-list-item>
    </ul>

    <SearchUserForm
      class="mr-2"
      @submit="searchAndScrollToMember" />

    <ul
      v-for="department in sortedAccessibleDepartments"
      :key="department.id"
      class="members__body list-unstyled">
      <DepartmentListItemMenu
        :ref="department.id"
        :department="department"
        :show-members="isShowedDepartment(department.id)"
        class="mb-4"
        @click.native="onClickDepartmentListItemMenu(department.id)">
        <template #pin-icon>
          <div
            class="pin-icon-button"
            @click.stop="togglePin(department.id)">
            <pin-icon :is-pinned="isPinned(department.id)" />
          </div>
        </template>
        <DepartmentMemberListItem
          v-for="member in membersByDepartmentId[department.id]"
          :key="member.id"
          :ref="member.id"
          :member="member"
          :is-searched="isSearched(member)">
          <template #default>
            <DailyWorkStackList
              :daily-work-list="dailyWorkListByMemberId[member.id]"
              :selected-date="selectedDate" />
          </template>
          <template #suffix>
            <SvgIcon
              class="member-list-item__chart-icon"
              :src="require('@/assets/images/icon_chart.svg')"
              :active="hasSubmittedMandalaEvaluation(member.id)" />
          </template>
        </DepartmentMemberListItem>
      </DepartmentListItemMenu>
    </ul>
    <ResultListScroller
      v-if="searchedMembers.length"
      :counts="searchedMembers.length"
      @onClickPrev="scrollToPrev"
      @onClickNext="scrollToNext"
      @onClickClose="resetSearchResult" />
  </BasePageContainer>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import UserProfileListItem from '@/components/view/UserProfileListItem.vue'
import DepartmentListItemMenu from '@/components/view/DepartmentListItemMenu.vue'
import DepartmentMemberListItem from '@/components/view/DepartmentMemberListItem.vue'
import { addDays, addMonths, endOfMonth, format, startOfMonth } from 'date-fns'
import { groupBy, uniq } from 'lodash'
import DailyWorkStackList from '@/components/view/DailyWorkStackList.vue'
import SvgIcon from '@/components/parts/SvgIcon.vue'
import PinIcon from '@/components/parts/PinIcon.vue'
import SearchUserForm from '@/components/view/SearchUserForm.vue'
import ResultListScroller from '@/components/parts/ResultListScroller.vue'

export default {
  components: {
    UserProfileListItem,
    DepartmentListItemMenu,
    DepartmentMemberListItem,
    DailyWorkStackList,
    SvgIcon,
    PinIcon,
    SearchUserForm,
    ResultListScroller
  },
  data() {
    return {
      selectedDate: new Date(),
      mandalaEvaluationStatuses: [],
      evaluationsStatusList: [],
      mandalaEvaluationStatusesByDepartmentId: {},
      dailyWorkListByDepartmentId: {},
      showedDepartmentIds: [],
      membersByDepartmentId: {},
      searchedMembers: [],
      currentSearchedMembersIndex: 0
    }
  },
  computed: {
    ...mapState('departments', ['pinnedDepartments']),
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['accessibleDepartments']),
    _pinnedDepartments: {
      get() {
        return this.pinnedDepartments
      },
      set(v) {
        this.$store.commit('departments/setPinnedDepartments', v)
      }
    },
    sortedAccessibleDepartments() {
      const [pinnedDepartments, restDepartments] = this.accessibleDepartments.reduce((prev, curr) => {
        if (this.pinnedDepartments.includes(curr.id)) {
          prev[0].push(curr)
        } else {
          prev[1].push(curr)
        }
        return prev
      }, [[], []])
      return [...pinnedDepartments, ...restDepartments]
    },
    sortedAccessibleDepartmentIds() {
      return this.sortedAccessibleDepartments.map(department => department.id)
    },
    flattenDailyWorkList() {
      return Object.values(this.dailyWorkListByDepartmentId).flat()
    },
    submittedMandalaEvaluationWorks() {
      const flattenMandalaEvaluationStatuses = Object.values(this.mandalaEvaluationStatusesByDepartmentId).flat()
      const submittedMandalaEvaluationStatuses = flattenMandalaEvaluationStatuses.filter(
        evaluationStatus => evaluationStatus.status === 'submitted'
      )
      return submittedMandalaEvaluationStatuses.map(
        evaluation => ({ createdBy: evaluation.user.id, createdDt: evaluation.submittedAt })
      )
    },
    submittedMandalaEvaluationWorksByMemberId() {
      return groupBy(this.submittedMandalaEvaluationWorks, 'createdBy')
    },
    /**
     * @description 今月の自己評価の提出日をdailyWorkListに追加
     */
    dailyWorkListWithSubmittedMandalaEvaluationWorks() {
      return [...this.flattenDailyWorkList, ...this.submittedMandalaEvaluationWorks]
    },
    dailyWorkListByMemberId () {
      return groupBy(this.dailyWorkListWithSubmittedMandalaEvaluationWorks, 'createdBy')
    },
    startDateOfMonth() {
      return startOfMonth(this.selectedDate)
    },
    endDateOfMonth() {
      return endOfMonth(this.selectedDate)
    },
    selectedYYYYMM() {
      return format(this.selectedDate, 'yyyyMM')
    },
    lastYearMonthStr() {
      return format(startOfMonth(addMonths(this.selectedDate, -1)), 'yyyy-MM-dd')
    },
    mandalaEvaluationStatusesByMemberId() {
      return groupBy(this.mandalaEvaluationStatuses, evaluation => evaluation.user.id)
    },
    // $elがあるため、レンダリング終了まで計算が終わらない -> $nextTickでラップする必要がある
    sortedSearchedMembers() {
      return [...this.searchedMembers].sort((a, b) => {
        const aTop = this.$refs[a.id][0].$el.getBoundingClientRect().top
        const bTop = this.$refs[b.id][0].$el.getBoundingClientRect().top
        if (aTop <= bTop) {
          return -1
        } else {
          return 1
        }
      })
    },
    searchedMembersDepartmentIds() {
      return uniq(this.searchedMembers.map(member => member.department.id))
    }
  },
  watch: {
    // 月が変わった時
    async selectedYYYYMM() {
      this.$store.commit('startLoading')
      // メンバー一覧が見えている全てのグループでrefetchする
      await Promise.all(
        this.showedDepartmentIds.map(departmentId => this.refetchByDepartmentId(departmentId))
      )
      this.$store.commit('finishLoading')
    }
  },
  async created() {
    this.$store.commit('startLoading')
    await this.$store.dispatch('auth/updateUser')
    this.$store.commit('finishLoading')
  },
  methods: {
    isShowedDepartment(departmentId) {
      return this.showedDepartmentIds.includes(departmentId)
    },
    async searchAndScrollToMember(searchKeyword) {
      // メンバーを検索する
      const res = await this.$axios.get('/users/', {
        params: {
          search: searchKeyword,
          ordering: '-sort_no',
          filter_accessible_departments: true
        }
      }).catch(error => {
        console.log(error)
        this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合はログインページ下のアプリ更新を押してください。'})
      })

      this.searchedMembers = res.data.results

      if (this.searchedMembersDepartmentIds.length === 0) {
        this.$store.commit('notify/set', {message: 'メンバーが見つかりませんでした'})
        return
      }

      await Promise.all(
        this.searchedMembersDepartmentIds.map(departmentId => this.refetchByDepartmentId(departmentId))
      )
      // メンバーが所属するグループを全て展開
      this.showedDepartmentIds = Array.from(new Set([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds]))
      
      // 検索されたメンバーのうち、画面上での最初のメンバーの要素にスクロール
      this.$nextTick(() => {
        const topMember = this.searchedMembers.reduce((prev, curr) => {
          const currentTop = this.$refs[curr.id][0].$el.getBoundingClientRect().top
          const prevTop = this.$refs[prev.id][0].$el.getBoundingClientRect().top
          return currentTop < prevTop ? curr : prev
        }, this.searchedMembers[0])
        this.scrollToMember(topMember)
      })
    },
    scrollToMember(member) {
      const memberEl = this.$refs[member.id][0].$el
      const targetTop = memberEl.getBoundingClientRect().top + window.pageYOffset - 20
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      })
    },
    hasSubmittedMandalaEvaluation(memberId) {
      return this.submittedMandalaEvaluationWorksByMemberId[memberId] !== undefined
    },
    onClickCalendarBtn(event) {
      if (event.target.innerText === 'arrow_left') {
        this.selectedDate = addDays(this.selectedDate, -1)
      }
      if (event.target.innerText === 'arrow_right') {
        this.selectedDate = addDays(this.selectedDate, 1)
      }
    },
    fetchMembersByDepartmentId(departmentId) {
      return this.$axios.get('/users/', {
        params: {
          ordering: '-sort_no',
          limit: 99999,
          department__in: departmentId
        }
      }).then(res => {
        this.$set(this.membersByDepartmentId, departmentId, res.data.results)
      })
    },
    fetchDailyWorkListByDepartmentId(departmentId) {
      return this.$axios.get('/activities/daily_work_list/', {
        params: {
          limit: 9999999,
          created_at__gte: this.startDateOfMonth,
          created_at__lte: this.endDateOfMonth,
          department: departmentId,
        }
      }).then(res => {
        this.$set(this.dailyWorkListByDepartmentId, departmentId, res.data)
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
    },
    fetchMandalaEvaluationStatusesByDepartmentId(departmentId) {
      return this.$axios.post('/mandala_goal_self_evaluations/user_status/', {
        department: departmentId,
        year_month: this.lastYearMonthStr,
      }).then(res => {
        this.$set(this.mandalaEvaluationStatusesByDepartmentId, departmentId, res.data)
      })
    },
    refetchByDepartmentId(departmentId) {
      return Promise.all([
        this.fetchMembersByDepartmentId(departmentId),
        this.fetchDailyWorkListByDepartmentId(departmentId),
        this.fetchMandalaEvaluationStatusesByDepartmentId(departmentId)
      ])
    },
    onClickDepartmentListItemMenu(departmentId) {
      if (this.showedDepartmentIds.includes(departmentId)) {
        this.showedDepartmentIds = this.showedDepartmentIds.filter(showedDepartmentId => showedDepartmentId !== departmentId)
      } else {
        this.refetchByDepartmentId(departmentId)
        this.showedDepartmentIds.push(departmentId)
      }
    },
    togglePin(departmentId) {
      if (this.isPinned(departmentId)) {
        this._pinnedDepartments = this._pinnedDepartments.filter(pinnedId => pinnedId !== departmentId)
      } else {
        this._pinnedDepartments = [...this._pinnedDepartments, departmentId]
      }
    },
    isPinned(departmentId) {
      return this.$store.getters['departments/isPinned'](departmentId)
    },
    isSearched(member) {
      return this.searchedMembers.map(member => member.id).includes(member.id)
    },
    scrollToPrev() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = this.currentSearchedMembersIndex === 0 ? this.searchedMembers.length - 1 : this.currentSearchedMembersIndex - 1
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    scrollToNext() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = (this.currentSearchedMembersIndex + 1) % this.searchedMembers.length
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    resetSearchResult() {
      this.searchedMembers = []
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/stylesheets/members';
@import '@/assets/stylesheets/members-calendar';

.calendar {
  bottom: -10px;
  right: 0;
}

.members {
  &__chart-icon {
    width: 24px;
    height: 24px;
    fill: currentColor;
    color: $grey02;
    &.active {
      color: $l-blue;
    }
  }
}

.member-list-item {
  &__chart-icon {
    color: $grey03;
  }
}

.pin-icon-button {
  margin-top: -27px;
  margin-right: -7px;

  .pin-icon {
    width: 15px;
  }
}

.result-list-scroller {
  bottom: calc(env(safe-area-inset-bottom) * 2 + 70px);
  right: 25px;
}
</style>

<style>
#datepicker__outer_.form-control {
  background-color: transparent;
}
#datepicker__outer_ > .form-control {
  color: white;
  background-color: transparent !important;
  border: none;
}
</style>